import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { crmApi } from "../api/client";
import { useSelectedProject } from "../contexts/SelectedProjectProvider";
import { useSelectedCompany } from "../contexts/SelectedCompanyProvider";

const LEADS_KEY = ["crm", "leads"];

// All optional. `filters` is the bag the SavedView system persists; flat at the
// network layer so the backend can parse cleanly.
function leadParams({ query, status, sort, order, limit, skip, filters = {}, projectId, companyQueryParams = {} }) {
  const params = { limit, skip, sort, order, ...companyQueryParams };
  if (query) params.q = query;
  if (status) params.status = status;
  if (filters.ownerIds?.length) params.ownerIds = filters.ownerIds.join(",");
  if (filters.sources?.length) params.sources = filters.sources.join(",");
  if (filters.labels?.length) params.labels = filters.labels.join(",");
  if (filters.updatedFrom) params.updatedFrom = filters.updatedFrom;
  if (filters.updatedTo) params.updatedTo = filters.updatedTo;
  if (projectId) params.projectId = projectId;
  return params;
}

export function useLeads({ query = "", status, limit = 50, skip = 0, sort = "updatedAt", order = "desc", filters } = {}) {
  const { projectId } = useSelectedProject();
  const { companyQueryParams } = useSelectedCompany();
  return useQuery({
    queryKey: [...LEADS_KEY, { query, status, limit, skip, sort, order, filters, projectId, companyQueryParams }],
    queryFn: async () => {
      const { data } = await crmApi.get("/leads", { params: leadParams({ query, status, sort, order, limit, skip, filters, projectId, companyQueryParams }) });
      return data;
    },
    keepPreviousData: true,
  });
}

// Distinct sources + labels in the current project — drives the Source and
// Labels filter dropdowns so the user picks instead of types. Cached for a
// few minutes since these change slowly relative to a filtering session.
export function useLeadFilterOptions() {
  const { projectId } = useSelectedProject();
  const { companyQueryParams } = useSelectedCompany();
  return useQuery({
    queryKey: ["crm", "leads", "filter-options", { projectId, companyQueryParams }],
    queryFn: async () => {
      const { data } = await crmApi.get("/leads/filter-options", {
        params: { ...(projectId ? { projectId } : {}), ...companyQueryParams },
      });
      return data;
    },
    staleTime: 5 * 60_000,
  });
}

// One-shot fetcher for CSV export — bypasses the query cache because we want
// the full filtered set, not the current page. Caller handles paging via skip.
export async function fetchLeadsPage({ query, status, sort, order, limit, skip, filters, projectId, companyQueryParams = {} }) {
  const { data } = await crmApi.get("/leads", { params: leadParams({ query, status, sort, order, limit, skip, filters, projectId, companyQueryParams }) });
  return data;
}

// Debounced duplicate check used by the create form. Returns up to 5 leads
// matching the typed email/phone/brandName. Disabled until at least one of
// those fields has a non-trivial value so we don't spam the server on every
// keystroke in the Title field.
export function useDuplicateCheck({ email, phone, brandName, excludeId } = {}) {
  const enough =
    (email && email.includes("@")) ||
    (phone && phone.replace(/[^0-9+]/g, "").length >= 7) ||
    (brandName && brandName.trim().length >= 3);
  return useQuery({
    queryKey: ["crm", "leads", "duplicate-check", { email, phone, brandName, excludeId }],
    enabled: !!enough,
    staleTime: 30_000,
    queryFn: async () => {
      const params = {};
      if (email) params.email = email;
      if (phone) params.phone = phone;
      if (brandName) params.brandName = brandName;
      if (excludeId) params.excludeId = excludeId;
      const { data } = await crmApi.get("/leads/duplicate-check", { params });
      return data?.matches || [];
    },
  });
}

// Bulk duplicate check for CSV import preview. Pass the parsed rows; the server
// returns one `{ matches: [...] }` entry per row, in order. Disabled until rows
// are present so the import flow doesn't fire it on every keystroke.
export function useBulkDuplicateCheck() {
  return useMutation({
    mutationFn: async (rows) => {
      const { data } = await crmApi.post("/leads/duplicate-check-bulk", { rows });
      return data?.results || [];
    },
  });
}

export function useCloneLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (leadId) => (await crmApi.post(`/leads/${leadId}/clone`)).data,
    onSuccess: (data) => invalidateAll(qc, data?.lead?._id),
  });
}

// Single-lead, single-target copy. The caller iterates over multiple targets
// when the user selects more than one project in the modal. On 409
// DUPLICATE_IN_TARGET we re-throw with a normalized shape so the modal can
// prompt for "Copy anyway".
export function useCopyLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadId, targetProjectId, confirmDuplicate }) => {
      try {
        const { data } = await crmApi.post(`/leads/${leadId}/copy`, { targetProjectId, confirmDuplicate });
        return data;
      } catch (err) {
        const data = err?.response?.data;
        if (err?.response?.status === 409 && data?.error === 'DUPLICATE_IN_TARGET') {
          const wrapped = new Error(data.message || 'Duplicate in target');
          wrapped.status = 409;
          wrapped.code = 'DUPLICATE_IN_TARGET';
          wrapped.existingLead = data.existingLead || null;
          throw wrapped;
        }
        throw err;
      }
    },
    onSuccess: (data) => invalidateAll(qc, data?.lead?._id),
  });
}

// Bulk copy — many leads → one target project. Caller iterates over target
// projects (modal supports picking multiple destinations). Server returns
// { copied, duplicates, failed, requested }.
export function useBulkCopyLeads() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadIds, targetProjectId, skipDuplicates, copyDuplicates }) =>
      (await crmApi.post('/leads/bulk-copy', { leadIds, targetProjectId, skipDuplicates, copyDuplicates })).data,
    onSuccess: () => invalidateAll(qc),
  });
}

export function useCrmHealth() {
  return useQuery({
    queryKey: ["crm", "health"],
    queryFn: async () => (await crmApi.get("/_health")).data,
    staleTime: 60_000,
  });
}

// `enabled` lets a caller hold the request until it is actually needed — the
// leads table only fetches a lead's history once you hover its follow-up cell,
// so opening the page doesn't fire one request per row.
export function useLeadActivities(leadId, { enabled = true, limit = 150 } = {}) {
  return useQuery({
    queryKey: ["crm", "activities", leadId, limit],
    enabled: !!leadId && enabled,
    queryFn: async () => (await crmApi.get(`/leads/${leadId}/activities`, { params: { limit } })).data,
  });
}

// AI "next best action" card atop the Activity tab. Server persists the
// suggestion onto the Lead doc, so re-opening the drawer shows the cached
// suggestion without re-running the AI — same pattern as the meetings rollup.
export function useLeadNextAction(leadId) {
  return useQuery({
    queryKey: ["crm", "next-action", leadId],
    enabled: !!leadId,
    queryFn: async () => (await crmApi.get(`/leads/${leadId}/next-action`)).data,
    staleTime: 60_000,
  });
}

export function useGenerateLeadNextAction(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await crmApi.post(`/leads/${leadId}/next-action`)).data,
    onSuccess: (data) => {
      qc.setQueryData(["crm", "next-action", leadId], {
        ok: true,
        nextAction: data.nextAction,
        summary: data.summary,
        actionItems: data.actionItems,
        generatedAt: data.generatedAt,
        model: data.model,
        error: null,
      });
    },
  });
}

// Centralized invalidator. Any mutation that touches a lead also moves the
// numbers on the dashboard, the org-wide activity feed, and may change a
// lead's pipeline-board visibility (won/lost moves a card off the board) and
// onboarding eligibility — so we fan out to all of those keys.
function invalidateAll(qc, leadId) {
  qc.invalidateQueries({ queryKey: LEADS_KEY });
  qc.invalidateQueries({ queryKey: ["crm", "pipelines"] });
  qc.invalidateQueries({ queryKey: ["crm", "dashboard", "overview"] });
  qc.invalidateQueries({ queryKey: ["crm", "activities", "feed"] });
  qc.invalidateQueries({ queryKey: ["crm", "followups", "active"] });
  qc.invalidateQueries({ queryKey: ["crm", "followups", "pending"] });
  if (leadId) {
    qc.invalidateQueries({ queryKey: ["crm", "activities", leadId] });
    qc.invalidateQueries({ queryKey: ["crm", "onboarding", leadId] });
  }
}

export function useCreateLead() {
  const qc = useQueryClient();
  const { projectId } = useSelectedProject();
  const { companyQueryParams, usesCompanies } = useSelectedCompany();
  return useMutation({
    mutationFn: async (payload) => {
      let body = projectId && !payload?.projectId
        ? { ...payload, projectId }
        : payload;
      if (usesCompanies && companyQueryParams?.companyId && !companyQueryParams?.unassigned) {
        body = { ...body, companyId: companyQueryParams.companyId };
      }
      return (await crmApi.post("/leads", body, { params: companyQueryParams })).data;
    },
    onSuccess: (data) => invalidateAll(qc, data?.lead?._id),
  });
}

export function useUpdateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }) => (await crmApi.patch(`/leads/${id}`, patch)).data,
    onSuccess: (data, vars) => invalidateAll(qc, vars.id),
  });
}

// Patch the coOwners array directly in every leads list cache entry that
// contains this lead. The drawer reads from items.find() so this makes the
// update visible immediately without waiting for the list refetch.
function patchCoOwnersInCache(qc, leadId, coOwners) {
  qc.setQueriesData({ queryKey: LEADS_KEY }, (old) => {
    if (!old?.items) return old;
    return {
      ...old,
      items: old.items.map((l) =>
        String(l._id) === String(leadId) ? { ...l, coOwners } : l
      ),
    };
  });
}

export function useAddCoOwner(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, email, name }) =>
      (await crmApi.post(`/leads/${leadId}/co-owners`, { userId, email, name })).data,
    onSuccess: (data) => {
      if (data?.lead?.coOwners) patchCoOwnersInCache(qc, leadId, data.lead.coOwners);
      invalidateAll(qc, leadId);
    },
  });
}

export function useRemoveCoOwner(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId) =>
      (await crmApi.delete(`/leads/${leadId}/co-owners/${userId}`)).data,
    onSuccess: (data) => {
      if (data?.lead?.coOwners) patchCoOwnersInCache(qc, leadId, data.lead.coOwners);
      invalidateAll(qc, leadId);
    },
  });
}

export function useChangeLeadStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status, lostReason }) =>
      (await crmApi.patch(`/leads/${id}/status`, { status, lostReason })).data,
    onMutate: ({ id, status }) => {
      // Optimistically patch the lead status inside every cached pipelines query
      // so the card updates instantly without waiting for the server round-trip.
      qc.setQueriesData({ queryKey: ["crm", "pipelines"] }, (old) => {
        if (!old?.pipelines) return old;
        return {
          ...old,
          pipelines: old.pipelines.map((pipeline) => ({
            ...pipeline,
            stages: (pipeline.stages || []).map((stage) => ({
              ...stage,
              leads: (stage.leads || []).map((entry) =>
                String(entry.leadId) === String(id) || String(entry.lead?._id) === String(id)
                  ? { ...entry, addedAt: new Date().toISOString(), lead: { ...entry.lead, status } }
                  : entry
              ),
            })),
          })),
        };
      });
    },
    onSuccess: (data, vars) => invalidateAll(qc, vars.id),
  });
}

export function useDeleteLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await crmApi.delete(`/leads/${id}`)).data,
    onSuccess: (_data, id) => invalidateAll(qc, id),
  });
}

// Bulk create — server caps at 500 rows. Response shape:
//   { summary: { created, failed }, items, errors: [{index, reason}] }
export function useBulkCreateLeads() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rows) => (await crmApi.post(`/leads/bulk`, { rows })).data,
    onSuccess: () => invalidateAll(qc),
  });
}

// Bulk update — patch is restricted server-side to { status, ownerId, labels }.
// Returns { summary: { updated, skipped, notFound }, notFound }.
export function useBulkUpdateLeads() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadIds, patch }) =>
      (await crmApi.post(`/leads/bulk-update`, { leadIds, patch })).data,
    onSuccess: () => invalidateAll(qc),
  });
}

export function useBulkDeleteLeads() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (leadIds) => (await crmApi.post(`/leads/bulk-delete`, { leadIds })).data,
    onSuccess: () => invalidateAll(qc),
  });
}

// Used by the undo-toast after bulk delete. Clears deletedAt server-side.
export function useBulkRestoreLeads() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (leadIds) => (await crmApi.post(`/leads/bulk-restore`, { leadIds })).data,
    onSuccess: () => invalidateAll(qc),
  });
}

// Notes ------------------------------------------------------------
export function useLeadNotes(leadId) {
  return useQuery({
    queryKey: ["crm", "notes", leadId],
    enabled: !!leadId,
    queryFn: async () => (await crmApi.get(`/leads/${leadId}/notes`)).data,
  });
}

export function useCreateNote(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => (await crmApi.post(`/leads/${leadId}/notes`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm", "notes", leadId] });
      qc.invalidateQueries({ queryKey: ["crm", "activities", leadId] });
    },
  });
}

export function useDeleteNote(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (noteId) => (await crmApi.delete(`/leads/${leadId}/notes/${noteId}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm", "notes", leadId] });
      qc.invalidateQueries({ queryKey: ["crm", "activities", leadId] });
    },
  });
}

// Contacts ---------------------------------------------------------
export function useLeadContacts(leadId) {
  return useQuery({
    queryKey: ["crm", "contacts", leadId],
    enabled: !!leadId,
    queryFn: async () => (await crmApi.get(`/leads/${leadId}/contacts`)).data,
  });
}

export function useCreateContact(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => (await crmApi.post(`/leads/${leadId}/contacts`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm", "contacts", leadId] });
      qc.invalidateQueries({ queryKey: ["crm", "activities", leadId] });
    },
  });
}

export function useUpdateContact(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }) => (await crmApi.patch(`/leads/${leadId}/contacts/${id}`, patch)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm", "contacts", leadId] });
      qc.invalidateQueries({ queryKey: ["crm", "activities", leadId] });
    },
  });
}

export function useDeleteContact(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await crmApi.delete(`/leads/${leadId}/contacts/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm", "contacts", leadId] });
      qc.invalidateQueries({ queryKey: ["crm", "activities", leadId] });
    },
  });
}

// Milestones -------------------------------------------------------
export function useLeadMilestones(leadId) {
  return useQuery({
    queryKey: ["crm", "milestones", leadId],
    enabled: !!leadId,
    queryFn: async () => (await crmApi.get(`/leads/${leadId}/milestones`)).data,
  });
}

function invalidateMilestones(qc, leadId) {
  qc.invalidateQueries({ queryKey: ["crm", "milestones", leadId] });
  qc.invalidateQueries({ queryKey: ["crm", "activities", leadId] });
}

export function useCreateMilestone(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => (await crmApi.post(`/leads/${leadId}/milestones`, payload)).data,
    onSuccess: () => invalidateMilestones(qc, leadId),
  });
}

export function useUpdateMilestone(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }) => (await crmApi.patch(`/leads/${leadId}/milestones/${id}`, patch)).data,
    onSuccess: () => invalidateMilestones(qc, leadId),
  });
}

export function useDeleteMilestone(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await crmApi.delete(`/leads/${leadId}/milestones/${id}`)).data,
    onSuccess: () => invalidateMilestones(qc, leadId),
  });
}

// Documents / Files ------------------------------------------------
export function useLeadDocuments(leadId) {
  return useQuery({
    queryKey: ["crm", "documents", leadId],
    enabled: !!leadId,
    queryFn: async () => (await crmApi.get(`/leads/${leadId}/documents`)).data,
  });
}

function invalidateDocuments(qc, leadId) {
  qc.invalidateQueries({ queryKey: ["crm", "documents", leadId] });
  qc.invalidateQueries({ queryKey: ["crm", "activities", leadId] });
}

export function useUploadDocument(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file) => {
      const form = new FormData();
      form.append("file", file);
      return (await crmApi.post(`/leads/${leadId}/documents`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      })).data;
    },
    onSuccess: () => invalidateDocuments(qc, leadId),
  });
}

export function useDeleteDocument(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (docId) => (await crmApi.delete(`/leads/${leadId}/documents/${docId}`)).data,
    onSuccess: () => invalidateDocuments(qc, leadId),
  });
}

export async function fetchDocumentDownloadUrl(leadId, docId) {
  const { data } = await crmApi.get(`/leads/${leadId}/documents/${docId}/download`);
  if (!data?.ok || !data?.url) {
    throw new Error(data?.error || "download failed");
  }
  return { url: data.url, filename: data.filename, mimeType: data.mimeType };
}

// Onboarding -------------------------------------------------------
// staleTime + refetch-on-focus disabled so the list doesn't silently
// refresh while the user is mid-toggle (caused a visible shuffle when items
// re-rendered from a fresh server payload).
export function useLeadOnboarding(leadId) {
  return useQuery({
    queryKey: ["crm", "onboarding", leadId],
    enabled: !!leadId,
    queryFn: async () => (await crmApi.get(`/leads/${leadId}/onboarding`)).data,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

function invalidateOnboarding(qc, leadId) {
  qc.invalidateQueries({ queryKey: ["crm", "onboarding", leadId] });
  qc.invalidateQueries({ queryKey: ["crm", "activities", leadId] });
  qc.invalidateQueries({ queryKey: LEADS_KEY }); // onboardedAt may have moved
}

export function useCreateOnboardingItem(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => (await crmApi.post(`/leads/${leadId}/onboarding`, payload)).data,
    onSuccess: () => invalidateOnboarding(qc, leadId),
  });
}

export function useUpdateOnboardingItem(leadId) {
  const qc = useQueryClient();
  const key = ["crm", "onboarding", leadId];
  return useMutation({
    mutationFn: async ({ id, patch }) => (await crmApi.patch(`/leads/${leadId}/onboarding/${id}`, patch)).data,
    // Optimistic: flip the item locally before the network round-trip so the
    // list doesn't shuffle/flicker on toggle. We touch the same fields the
    // server would (completedAt, title, notes); on error we roll back to the
    // snapshot we took here.
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData(key);
      qc.setQueryData(key, (cur) => {
        if (!cur?.items) return cur;
        const items = cur.items.map((it) => {
          if (String(it._id) !== String(id)) return it;
          const next = { ...it };
          if (patch?.completed !== undefined) {
            next.completedAt = patch.completed ? (it.completedAt || new Date().toISOString()) : null;
          }
          if (patch?.title !== undefined) next.title = patch.title;
          if (patch?.notes !== undefined) next.notes = patch.notes;
          return next;
        });
        return { ...cur, items };
      });
      return { prev };
    },
    // Merge server response into local cache directly — no refetch. Mirrors
    // what the server saved without forcing the whole list to refresh (which
    // was causing the visible shuffle/flicker on every toggle).
    onSuccess: (resp) => {
      const serverItem = resp?.item;
      if (!serverItem?._id) return;
      qc.setQueryData(key, (cur) => {
        if (!cur?.items) return cur;
        return {
          ...cur,
          items: cur.items.map((it) => (String(it._id) === String(serverItem._id) ? { ...it, ...serverItem } : it)),
        };
      });
      // Activity feed + leads list still need to know (new ONBOARDING_COMPLETED
      // row, onboardedAt may flip). Onboarding list is NOT invalidated — local
      // cache is already in sync via setQueryData above.
      qc.invalidateQueries({ queryKey: ["crm", "activities", leadId] });
      qc.invalidateQueries({ queryKey: LEADS_KEY });
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
  });
}

export function useDeleteOnboardingItem(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await crmApi.delete(`/leads/${leadId}/onboarding/${id}`)).data,
    onSuccess: () => invalidateOnboarding(qc, leadId),
  });
}

// Explicit "Mark as onboarded" — backend enforces pct === 100 before stamping
// lead.onboardedAt and emitting the ONBOARDING_FINISHED activity row.
export function useMarkLeadOnboarded(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await crmApi.post(`/leads/${leadId}/onboarding/finish`)).data,
    onSuccess: () => invalidateOnboarding(qc, leadId),
  });
}

export function useUnmarkLeadOnboarded(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await crmApi.delete(`/leads/${leadId}/onboarding/finish`)).data,
    onSuccess: () => invalidateOnboarding(qc, leadId),
  });
}

// Calls ------------------------------------------------------------
// Calls list polls every 10s while the panel is open so live status updates
// (ringing → in-progress → completed) and the AI summary appear without the
// user refreshing. Source CRM did the same.
export function useCallLogs(leadId) {
  return useQuery({
    queryKey: ["crm", "calls", leadId],
    enabled: !!leadId,
    queryFn: async () => (await crmApi.get(`/leads/${leadId}/calls`)).data,
    refetchInterval: (q) => (q.state.data && typeof document !== "undefined" && document.hidden ? false : 10_000),
    refetchIntervalInBackground: false,
  });
}

// Back-compat alias — earlier panel code imports useLeadCalls.
export const useLeadCalls = useCallLogs;

function invalidateCalls(qc, leadId) {
  qc.invalidateQueries({ queryKey: ["crm", "calls", leadId] });
  qc.invalidateQueries({ queryKey: ["crm", "activities", leadId] });
}

export function useInitiateCall(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) =>
      (await crmApi.post(`/leads/${leadId}/calls`, payload)).data,
    onSuccess: () => invalidateCalls(qc, leadId),
  });
}

export function useUpdateCallNote(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ callId, title, description }) =>
      (await crmApi.patch(`/leads/${leadId}/calls/${callId}/note`, { title, description })).data,
    onSuccess: () => invalidateCalls(qc, leadId),
  });
}

export function useRegenerateSummary(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (callId) =>
      (await crmApi.post(`/leads/${leadId}/calls/${callId}/regenerate-summary`)).data,
    onSuccess: () => invalidateCalls(qc, leadId),
  });
}

// Cached master-summary across all calls for a lead. GET returns whatever's
// stored (null if never generated); POST regenerates with the current set of
// calls. Stored on the lead doc so it survives panel re-mounts.
export function useLeadCallsRollup(leadId) {
  return useQuery({
    queryKey: ["crm", "calls", leadId, "rollup"],
    enabled: !!leadId,
    queryFn: async () => (await crmApi.get(`/leads/${leadId}/calls/rollup-summary`)).data,
    staleTime: 60_000,
  });
}

export function useGenerateLeadCallsRollup(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await crmApi.post(`/leads/${leadId}/calls/rollup-summary`)).data,
    onSuccess: (data) => {
      // Hydrate the cache so the panel updates immediately, before any refetch.
      qc.setQueryData(["crm", "calls", leadId, "rollup"], {
        ok: true,
        summary: data.summary,
        generatedAt: data.generatedAt,
        callsIncluded: data.callsIncluded,
        error: null,
      });
    },
  });
}

export function useDeleteCall(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (callId) =>
      (await crmApi.delete(`/leads/${leadId}/calls/${callId}`)).data,
    onSuccess: () => invalidateCalls(qc, leadId),
  });
}

// Fetches short-lived browser SDK credentials for the current agent.
export async function fetchPlivoBrowserCredentials(leadId) {
  const { data } = await crmApi.post(`/leads/${leadId}/calls/plivo-token`);
  return data;
}

// Lead-free variant for the always-on incoming-call listener. Same org-shared
// SIP endpoint credentials, no leadId required.
export async function fetchPlivoListenerCredentials() {
  const { data } = await crmApi.post(`/settings/plivo-token`);
  return data;
}

// Authenticated recording stream URL. The <audio> element can't send our JWT
// header, so we return a fetch-then-blob URL builder; callers create the
// blob URL on demand and revoke it when unmounting.
export async function fetchRecordingBlobUrl(leadId, callId) {
  const res = await crmApi.get(`/leads/${leadId}/calls/${callId}/recording`, {
    responseType: "blob",
  });
  return URL.createObjectURL(res.data);
}

// Org call settings (Plivo numbers) -------------------------------
const CALL_NUMBERS_KEY = ["crm", "settings", "call-numbers"];

export function useOrgCallNumbers() {
  return useQuery({
    queryKey: CALL_NUMBERS_KEY,
    queryFn: async () => (await crmApi.get("/settings/call-numbers")).data,
  });
}

export function useAddOrgCallNumber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => (await crmApi.post("/settings/call-numbers", payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: CALL_NUMBERS_KEY }),
  });
}

export function useUpdateOrgCallNumber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ numberId, patch }) =>
      (await crmApi.patch(`/settings/call-numbers/${numberId}`, patch)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: CALL_NUMBERS_KEY }),
  });
}

export function useDeleteOrgCallNumber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (numberId) =>
      (await crmApi.delete(`/settings/call-numbers/${numberId}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: CALL_NUMBERS_KEY }),
  });
}

// Inbound fallback number — the PSTN number Plivo dials when an incoming
// call's browser leg goes unanswered.
const INBOUND_FALLBACK_KEY = ["crm", "settings", "inbound-fallback"];

export function useInboundFallback() {
  return useQuery({
    queryKey: INBOUND_FALLBACK_KEY,
    queryFn: async () => (await crmApi.get("/settings/inbound-fallback")).data,
  });
}

export function useUpdateInboundFallback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (inboundFallbackNumber) =>
      (await crmApi.patch("/settings/inbound-fallback", { inboundFallbackNumber })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: INBOUND_FALLBACK_KEY }),
  });
}
